import type { ProjectStageStatus } from './project-governance.js';

/** The 7 PRINCE2 tolerance areas. */
export const ToleranceArea = {
  Time: 'TIME',
  Cost: 'COST',
  Quality: 'QUALITY',
  Scope: 'SCOPE',
  Benefits: 'BENEFITS',
  Risk: 'RISK',
  Sustainability: 'SUSTAINABILITY',
} as const;
export const ToleranceAreaValues = Object.values(ToleranceArea);
export type ToleranceArea = (typeof ToleranceArea)[keyof typeof ToleranceArea];

/** The business layer sets the project level. The Executive sets the stage level. */
export const ToleranceLevel = {
  Project: 'PROJECT',
  Stage: 'STAGE',
} as const;
export const ToleranceLevelValues = Object.values(ToleranceLevel);
export type ToleranceLevel = (typeof ToleranceLevel)[keyof typeof ToleranceLevel];

export const ToleranceStatus = {
  Within: 'WITHIN',
  Warning: 'WARNING',
  ForecastException: 'FORECAST_EXCEPTION',
  /** No limit is set. The code never assumes a limit. */
  NotSet: 'NOT_SET',
  /** Data is missing or older than the update interval. Never shown as green. */
  CannotEvaluate: 'CANNOT_EVALUATE',
  NotApplicable: 'NOT_APPLICABLE',
} as const;
export const ToleranceStatusValues = Object.values(ToleranceStatus);
export type ToleranceStatus = (typeof ToleranceStatus)[keyof typeof ToleranceStatus];

/** Cost tolerance bounds are an amount in the project currency, or a percent of the budget. */
export const CostToleranceUnit = {
  Amount: 'AMOUNT',
  Percent: 'PERCENT',
} as const;
export const CostToleranceUnitValues = Object.values(CostToleranceUnit);
export type CostToleranceUnit = (typeof CostToleranceUnit)[keyof typeof CostToleranceUnit];

/**
 * Bounds by area:
 * - TIME: days relative to the planned end (lower is usually negative or null).
 * - COST: deviation from the budget, as `unit` AMOUNT or PERCENT.
 * - SCOPE: the number of MUST milestones that can miss the time limit (usually 0).
 * - RISK: `upperBound` is the maximum score (1-25) of a risk with no approved response.
 * - QUALITY, BENEFITS, SUSTAINABILITY: the allowed range of the measured `metricKey`; `unit` is a label.
 * `warningThreshold` is the percent of the TIME or COST tolerance that gives a warning.
 */
export interface ProjectToleranceInputDto {
  area: ToleranceArea;
  lowerBound?: number | null;
  upperBound?: number | null;
  unit?: string | null;
  warningThreshold?: number | null;
  metricKey?: string | null;
  notApplicable?: boolean;
  /** Required when `notApplicable` is true. */
  rationale?: string | null;
}

export interface ProjectToleranceDto {
  id: string;
  level: ToleranceLevel;
  stageId: string | null;
  area: ToleranceArea;
  lowerBound: number | null;
  upperBound: number | null;
  unit: string | null;
  warningThreshold: number | null;
  metricKey: string | null;
  notApplicable: boolean;
  rationale: string | null;
  setByUserId: string;
  updatedAt: string;
}

/** Replaces the tolerances of one level (and stage). Areas that are not in `items` are removed. */
export interface UpsertProjectTolerancesDto {
  expectedVersion: number;
  level: ToleranceLevel;
  /** Required for the STAGE level. */
  stageId?: string | null;
  items: ProjectToleranceInputDto[];
}

export interface ToleranceEvaluationDto {
  level: ToleranceLevel;
  stageId: string | null;
  area: ToleranceArea;
  status: ToleranceStatus;
  /** The current or forecast value. TIME uses an ISO date. */
  value: number | string | null;
  lowerLimit: number | string | null;
  upperLimit: number | string | null;
  /** A code that explains NOT_SET, CANNOT_EVALUATE, or an exception. */
  reason: string | null;
  /** The age of the data that the result uses. */
  dataAt: string | null;
}

export const MilestonePriority = {
  Must: 'MUST',
  Optional: 'OPTIONAL',
} as const;
export const MilestonePriorityValues = Object.values(MilestonePriority);
export type MilestonePriority = (typeof MilestonePriority)[keyof typeof MilestonePriority];

export interface SetBaselineBudgetDto {
  expectedVersion: number;
  baselineBudget: number | null;
}

/** The ETC is an operational value. It does not change the governance version. */
export interface SetStageEstimateDto {
  estimateToComplete: number;
}

/** The forecast is an operational value. It does not change the governance version. */
export interface SetMilestoneForecastDto {
  forecastDueDate: string | null;
}

/** Duration is operational, like the forecast. It does not change the governance version. */
export interface SetMilestoneDurationDto {
  durationWorkingDays: number | null;
}

export interface SetMilestonePriorityDto {
  expectedVersion: number;
  priority: MilestonePriority;
}

export interface CostFiguresDto {
  actual: number;
  openCommitments: number;
}

export interface StageControlsDto {
  stageId: string;
  actual: number | null;
  openCommitments: number | null;
  title: string;
  status: ProjectStageStatus;
  plannedStart: string | null;
  plannedEnd: string | null;
  plannedBudget: number | null;
  estimateToComplete: number | null;
  etcUpdatedAt: string | null;
  /** actual + open commitments + ETC; null when the ETC is not entered. */
  eac: number | null;
}

export interface PlanToDateDto {
  plannedToDate: number;
  actualToDate: number;
  /** actualToDate - plannedToDate. */
  variance: number;
}

/**
 * Budgets, forecast, and tolerance status of a governed project.
 * Supplier-side users get no financial figures: the money fields are null and COST is omitted.
 */
export interface ProjectControlsDto {
  currency: string;
  financialVisible: boolean;
  baselineBudget: number | null;
  /** baselineBudget - the sum of the stage budgets. */
  unallocatedBudget: number | null;
  stages: StageControlsDto[];
  /** Costs with no stage. */
  unassigned: CostFiguresDto | null;
  totals: (CostFiguresDto & { plannedBudget: number; estimateToComplete: number; eac: number | null }) | null;
  planToDate: PlanToDateDto | null;
  tolerances: ProjectToleranceDto[];
  evaluation: ToleranceEvaluationDto[];
  evaluatedAt: string;
  permissions: {
    canEditBaseline: boolean;
    canEditProjectTolerances: boolean;
    canEditStageTolerances: boolean;
    canEditEstimates: boolean;
  };
}
